/**
 * CUBE JUMP — cartridge palette.
 *
 * Q*bert heritage filtered through the cabinet's vaporwave-CRT aesthetic.
 * Coffee-shop tones: cream/espresso unpainted cubes flip to neon-cyan when
 * painted (the "coffee with froth" read). The bean is hot-orange to pop
 * against the cool cube field; the snake is magenta (high-contrast threat);
 * the balls are blood-red (consistent kill-you signal across the catalog).
 *
 * The deep espresso-violet sky with cyan grid lines gives the playfield
 * the "tile floating in cyberspace" feel that lets the pyramid sit cleanly
 * without a horizon line.
 */

// ---- Sky / playfield background ----
export const SKY_TOP = '#1a0a28';        // deep espresso-violet at canvas top
export const SKY_BOTTOM = '#0a0418';     // near-black at canvas bottom
export const GRID_LINE = '#3a1850';      // faint cyan-tinted violet grid
export const GRID_GLOW = '#5a2880';      // intersection glow

// ---- HUD gutters ----
// Cartridge-owned chrome in the side gutters (the pyramid sits in a
// centered ~270-wide column per the landscape-only rule).
export const GUTTER_BG = '#160628';      // gutter base — slightly darker than sky
export const GUTTER_EDGE = '#2a1438';    // gutter inner edge
export const HUD_LABEL = '#7af3c8';      // cyan label, cabinet default
export const HUD_SCORE = '#ffea00';      // gold score numerals
export const HUD_LIVES = '#ff8030';      // matches bean body
export const HUD_LEVEL = '#a0fff0';      // pale cyan level number

// ---- Cubes ----
// Top face (the surface the bean lands on). Unpainted = espresso cream;
// painted = froth cyan. The mid-paint flash bursts white before settling.
export const CUBE_TOP_UNPAINTED = '#d8b890';   // creamy oat-milk top
export const CUBE_TOP_PAINTED = '#00f0c8';     // neon-cyan froth
export const CUBE_TOP_FLASH = '#ffffff';       // 4-tick paint flash

// Left face. Slightly darker than the top to read as iso.
export const CUBE_LEFT_UNPAINTED = '#6a3818';  // espresso brown
export const CUBE_LEFT_PAINTED = '#006a58';    // deep cyan-teal

// Right face. Darker still — iso shading.
export const CUBE_RIGHT_UNPAINTED = '#3a1c08'; // dark roast
export const CUBE_RIGHT_PAINTED = '#003a30';   // deepest teal

// Cube outline. Same tone whether painted or not.
export const CUBE_OUTLINE = '#0a0408';

// ---- Player (coffee bean) ----
// A bean is essentially two stacked rounded halves with a vertical crease.
// Hot-orange roasted shell with a cream highlight lifts off the cool cubes.
export const BEAN_BODY = '#ff8030';      // roasted shell
export const BEAN_HIGHLIGHT = '#ffd0a0'; // cream lift
export const BEAN_CREASE = '#8a3010';    // center crease shadow
export const BEAN_OUTLINE = '#3a1408';   // bean outline

// ---- Red ball (step 4) ----
// Q*bert's red ball — blood red with a white highlight that pulls
// "rolling" out of the simple circle. Distinct from the snake's
// magenta so the player reads two distinct threat sprites at a glance.
export const BALL_BODY = '#ff3030';      // blood red
export const BALL_DARK = '#8a1010';      // shadow / squash underside
export const BALL_HIGHLIGHT = '#ffd0d0'; // top-left rolling-ball lift
export const BALL_WARNING = '#ff3030';   // pre-spawn warning flash
export const BALL_WARNING_GLOW = '#ff8030'; // outer glow on the warning

// ---- Zone: PYRAMID BETA (step 5) ----
// Sapphire + gold variant of the cartridge palette. Player + threats
// stay the same hue (orange bean, magenta snake, red ball) so threat
// read is consistent across zones — only the playfield + cubes shift.
export const BETA_SKY_TOP = '#08182a';        // deep midnight blue
export const BETA_SKY_BOTTOM = '#020810';     // near-black
export const BETA_GRID_LINE = '#1a4080';      // sapphire grid
export const BETA_GRID_GLOW = '#3070d0';      // glow at lattice points

export const BETA_CUBE_TOP_UNPAINTED = '#a0c0e0';    // pale sapphire
export const BETA_CUBE_TOP_PAINTED = '#ffea00';      // hot gold (the cartridge's "treasure" read)
export const BETA_CUBE_LEFT_UNPAINTED = '#3068a8';   // mid sapphire
export const BETA_CUBE_LEFT_PAINTED = '#a08020';     // burnished gold-shadow
export const BETA_CUBE_RIGHT_UNPAINTED = '#1a3870';  // deep sapphire shadow
export const BETA_CUBE_RIGHT_PAINTED = '#605020';    // dark gold shadow

export const BETA_GUTTER_BG = '#060e1c';      // darker gutter to match deeper sky
export const BETA_GUTTER_EDGE = '#1a3060';    // sapphire inner edge

// ---- Snake enemy (step 3) ----
// Magenta against the cyan/cream pyramid for high-contrast threat read.
// Segmented body with a darker spine + bright eye glint so the player
// can see which way the snake is facing at a glance.
export const SNAKE_BODY = '#ff3088';     // hot magenta — kill-you signal
export const SNAKE_DARK = '#8a1850';     // segment shadow + spine
export const SNAKE_HIGHLIGHT = '#ff80c0';// belly / top highlight
export const SNAKE_EYE = '#ffea00';      // gold eye glint
export const SNAKE_FORKED_TONGUE = '#ff5050'; // tongue flick on hops

// ---- Danger card (snake spawn) ----
// Distinct from the cyan level-clear card — uses red borders + flashing
// to read as "incoming threat" rather than celebration.
export const DANGER_BG = '#280810';      // deep blood backdrop
export const DANGER_BORDER = '#ff3030';  // bright red border
export const DANGER_TEXT = '#ffea00';    // gold heading
export const DANGER_SUB = '#ff80a0';     // pale pink subtitle

// ---- Level-clear card ----
export const CARD_BG = '#1a0a28';        // sky-colored backdrop
export const CARD_BORDER = '#00f0c8';    // cyan border = "level clear"
export const CARD_TEXT = '#ffea00';      // gold heading
export const CARD_SUB = '#a0fff0';       // pale-cyan subtitle

// ---- Attract-mode chrome ----
export const ATTRACT_TITLE = '#ff8030';  // bean-orange "CUBE JUMP"
export const ATTRACT_PROMPT = '#7af3c8'; // cyan "PRESS START"
export const ATTRACT_DIM = '#2a1438';    // dimmer when attract idles

/**
 * Tokens the cabinet samples for marquee tinting while this cartridge is
 * loaded. First entry is `paletteHint[0]`, used everywhere the cabinet
 * needs a single "this is the cartridge's color" pick.
 */
export const PALETTE_HINT: readonly string[] = [
  CUBE_TOP_PAINTED, // the cartridge's signature froth-cyan
  BEAN_BODY,        // bean orange
  HUD_SCORE,        // gold score
  CARD_BG,          // espresso-violet sky
];
